/**
 * THE BLOCK UNION. ONE DEFINITION, EVERY CALLER.
 *
 * WHY IT IS HERE AND NOT IN `src/`. `02-nfr.md` puts "block union, converters, D18 anchors"
 * in this package because "the editor AND the moderation worker must compute identical anchors", and
 * `adr/0008` makes the consequence explicit: "`shared/content-model` becomes load-bearing: the
 * block union types, the `blocks ⇄ editor JSON` converter, the D18 anchor maths, and the client
 * mirror of **all eight of D21's rules** that CORE also enforces server-side." `adr/0008` is the
 * discipline every file in this package keeps - "One anchor implementation, two callers."
 *
 * ★ THESE SCHEMAS WERE LIFTED FROM `src/modules/articles/dto/article-editor.schema.ts`, WHICH NOW
 * IMPORTS THEM. That file's transcription of the contract was correct and is preserved sentence for
 * sentence below; what changes is that there is now ONE copy, which the SPA editor can import as
 * well. A second transcription is how the browser and the server come to disagree about what a
 * block is, and the disagreement shows up as a 400 the author cannot read.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * THE VOCABULARIES ARE THE SEEDED TABLES', NOT THIS FILE'S
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * `block_kind` seeds five rows (four in `04-schema.md`, `video` in 0144), `paragraph_style` seven
 * (`04-schema.md`, `WC-CR-09` ruled **(c)** - note 19's set plus `h1`), and
 * `inline_mark_kind` six (`04-schema.md`). Each list below is that seed, and the contract
 * declares the same three enums at `authors-api.yaml`. They are `z.enum`s here and not
 * lookups because a shape check is not a catalogue read: `article-version.writer.ts` INNER JOINs
 * `paragraph_style ps ON ps.code = src.style`, so a style outside the seeded seven would drop the
 * paragraph row silently instead of refusing the request.
 *
 * C11: no clock is read here and no instant is taken.
 */
import { z } from 'zod';

import { UUIDV7_RE } from './ids';

/** `block_kind.code`: the four v1-native kinds (`04-schema.md`) and `video` (0144). */
export const BLOCK_KINDS = ['text', 'quote', 'image', 'divider', 'video'] as const;
export type BlockKind = (typeof BLOCK_KINDS)[number];

/** How many video blocks an article may hold, of either source (the owner, 2 November 2026). */
export const MAX_VIDEO_BLOCKS = 3;

/** `paragraph_style.code`, all seven (`04-schema.md`, `WC-CR-09` ruled (c)). */
export const PARAGRAPH_STYLES = ['p', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol'] as const;
export type ParagraphStyle = (typeof PARAGRAPH_STYLES)[number];

/** The two styles `paragraph_style.is_list` is true for - "only a list style may carry list_level > 0". */
export const LIST_PARAGRAPH_STYLES = ['ul', 'ol'] as const;
export type ListParagraphStyle = (typeof LIST_PARAGRAPH_STYLES)[number];

/** The heading styles, deepest last. `h1` is here because `WC-CR-09` was ruled (c) and not (a). */
export const HEADING_PARAGRAPH_STYLES = ['h1', 'h2', 'h3', 'h4'] as const;
export type HeadingParagraphStyle = (typeof HEADING_PARAGRAPH_STYLES)[number];

/** `ck_awpara_level CHECK (list_level BETWEEN 0 AND 3)` - live, and `authors-api.yaml`. */
export const MAX_LIST_LEVEL = 3;

/** The default `paragraph_style` for a paragraph nobody styled. `DEFAULT` on no column; see below. */
export const DEFAULT_PARAGRAPH_STYLE: ParagraphStyle = 'p';

/** `inline_mark_kind.code`, all six (`04-schema.md`). */
export const INLINE_MARK_KINDS = ['strong', 'em', 'underline', 'strike', 'code', 'link'] as const;
export type InlineMarkKind = (typeof INLINE_MARK_KINDS)[number];

/** The one kind `inline_mark_kind.needs_href` is true for, read as data by `trg_awmark_href`. */
export const HREF_MARK_KIND: InlineMarkKind = 'link';

/**
 * `ck_awmark_href CHECK (link_href IS NULL OR link_href ~ '^(https?://|mailto:)')` - live, and the
 * contract's own `pattern` (`authors-api.yaml`). It is a database constraint rather than
 * an operator-editable list "because a scheme allowlist with an edit button on it is a security
 * control with an edit button on it".
 */
export const LINK_HREF_RE = /^(https?:\/\/|mailto:)/u;

/**
 * `Block.blockId`, `article_working_block.block_id`, `article_version_block.block_id`.
 *
 * `UUIDV7_RE` is imported rather than re-typed: `ids.ts` holds it beside
 * `UUIDV7_CHECK_CONSTRAINT_SOURCE`, the byte-for-byte copy of the DDL CHECK, so this schema and the
 * database cannot drift. Its own header states the trap - "a generator that stamps the version and
 * forgets RFC 9562's variant bits passes every naive `charAt(14) === '7'` check and then fails every
 * block insert".
 */
export const blockIdSchema = z
  .string()
  .regex(UUIDV7_RE, 'blockId must be a lowercase UUIDv7 (ck_awb_id_format)');

/** `ParagraphStyle` - `authors-api.yaml`, and the seven rows of `paragraph_style`. */
export const paragraphStyleSchema = z.enum(PARAGRAPH_STYLES);

/** `InlineMarkKind` - `authors-api.yaml`, and the six rows of `inline_mark_kind`. */
export const inlineMarkKindSchema = z.enum(INLINE_MARK_KINDS);

/**
 * `Paragraph` - `authors-api.yaml`.
 *
 * `endCp` HAS NO `min(1)` AND THAT IS THE CONTRACT: ":4253-4255" reads "Exclusive, and **may equal
 * `startCp` for an empty paragraph**", and the table agrees - `ck_awpara_range CHECK (start_cp >= 0
 * AND end_cp >= start_cp)`, verified live. An empty paragraph "keeps its row: an author who presses
 * Enter twice keeps the blank line, and an editor that deletes their spacing on save is reported as
 * data loss" (`:4219-4221`).
 *
 * `listLevel` carries the contract's `default: 0`, which the pipe applies - `Validated<S>` is
 * `z.output`, so a `.default()` has already been filled in by the time the handler sees it. The 0..3
 * band is one of "the two product limits held as a database constraint" (`:4239-4243`,
 * `ck_awpara_level` live), so it is checked twice on purpose.
 */
export const paragraphSchema = z.strictObject({
  paraIndex: z.int().min(0),
  startCp: z.int().min(0),
  endCp: z.int().min(0),
  style: paragraphStyleSchema,
  listLevel: z.int().min(0).max(MAX_LIST_LEVEL).default(0),
});

/**
 * `InlineMark` - `authors-api.yaml`.
 *
 * TWO RULES ARE CHECKED HERE AND BOTH ARE THE CONTRACT'S OWN WORDS.
 *   `endCp > startCp` - ":4258-4260", "Exclusive, and strictly greater than `startCp` - **a
 *   zero-width mark is not a mark**", and `ck_awmark_range` live.
 *   `linkHref` present exactly for `link` - ":4264-4265", "Required for `link` and forbidden
 *   otherwise", enforced in the database by `trg_awmark_href`, which reads
 *   `inline_mark_kind.needs_href` (true on exactly one of the six rows, verified live) rather than
 *   comparing a name - which is why `HREF_MARK_KIND` above is a constant naming that row and not a
 *   literal written into the refinement.
 */
export const inlineMarkSchema = z
  .strictObject({
    startCp: z.int().min(0),
    endCp: z.int().min(1),
    kind: inlineMarkKindSchema,
    linkHref: z
      .string()
      .regex(LINK_HREF_RE, 'linkHref must be an https, http or mailto address')
      .nullish(),
  })
  .refine((mark) => mark.endCp > mark.startCp, {
    error: 'endCp must be strictly greater than startCp: a zero-width mark is not a mark',
    path: ['endCp'],
  })
  .refine(
    (mark) =>
      (mark.kind === HREF_MARK_KIND) === (mark.linkHref !== null && mark.linkHref !== undefined),
    {
      error: 'linkHref is required for a link mark and forbidden on every other kind',
      path: ['linkHref'],
    },
  );

/**
 * The block kinds, as a DISCRIMINATED union on `kind` - which is the discriminator the contract
 * itself declares (`authors-api.yaml`, `discriminator: propertyName: kind`).
 *
 * A discriminated union rather than a plain one because the refusal is different: a plain union
 * reports every branch's failure for one bad block, and the author is told an image block failed to
 * be a divider. The database draws the same shapes as one CHECK each on one table -
 * `ck_awb_text`, `ck_awb_quote`, `ck_awb_image`, `ck_awb_divider`, `ck_awb_video` (live) - each keyed
 * on `block_kind_id`, which is what the branches below mirror.
 *
 * `url` IS ACCEPTED AND IGNORED ON AN IMAGE BLOCK. `authors-api.yaml` marks it
 * `readOnly: true` - "The public rendition. Read-only; the platform derives it." A `readOnly`
 * member is legal in a request under OpenAPI and must not be written, so it is parsed (to keep the
 * strict object from refusing a client that echoes back what it was served) and never read.
 *
 * ★ THE OFFSET RULES ARE NOT HERE. Whether a paragraph row tiles its block's text and whether a
 * mark lies inside it are questions about the TEXT, not about the shape, and they are asked by
 * `anchors.ts` - which is also what the server uses to recompute the rows. See
 * `article-editor.schema.ts`, where the two are put together at the request boundary.
 */
export const blockSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    blockId: blockIdSchema,
    kind: z.literal('text'),
    body: z.string(),
    paragraphs: z.array(paragraphSchema).optional(),
    marks: z.array(inlineMarkSchema).optional(),
  }),
  z.strictObject({
    blockId: blockIdSchema,
    kind: z.literal('quote'),
    body: z.string(),
    quoteAttribution: z.string().nullish(),
    marks: z.array(inlineMarkSchema).optional(),
  }),
  z.strictObject({
    blockId: blockIdSchema,
    kind: z.literal('image'),
    fileId: z.string().min(1).max(128),
    url: z.string().optional(),
    imageAlt: z.string().nullish(),
    caption: z.string().nullish(),
  }),
  z.strictObject({
    blockId: blockIdSchema,
    kind: z.literal('divider'),
  }),
  // A video block holds its id alone: `setArticleVideoBlock` writes the video beside it. `video` is
  // read-only, parsed for `url`'s reason above and never read.
  z.strictObject({
    blockId: blockIdSchema,
    kind: z.literal('video'),
    video: z.unknown().optional(),
  }),
]);

/** One paragraph row, as the contract parses it (`listLevel` already defaulted). */
export type Paragraph = z.output<typeof paragraphSchema>;
/** One mark row, as the contract parses it. */
export type InlineMark = z.output<typeof inlineMarkSchema>;
/** One block of the versioned union, as the contract parses it. */
export type Block = z.output<typeof blockSchema>;

export type TextBlock = Extract<Block, { kind: 'text' }>;
export type QuoteBlock = Extract<Block, { kind: 'quote' }>;
export type ImageBlock = Extract<Block, { kind: 'image' }>;
export type DividerBlock = Extract<Block, { kind: 'divider' }>;
export type VideoBlock = Extract<Block, { kind: 'video' }>;

/**
 * Is this a style that may carry a `list_level` above zero?
 *
 * Written as a predicate over `LIST_PARAGRAPH_STYLES` rather than `style === 'ul' || style === 'ol'`
 * so that the seeded `is_list` column has exactly one mirror in this package.
 */
export function isListStyle(style: ParagraphStyle): style is ListParagraphStyle {
  return (LIST_PARAGRAPH_STYLES as readonly ParagraphStyle[]).includes(style);
}

/** Is this a heading style, and at what depth? `null` for every other style. */
export function headingDepth(style: ParagraphStyle): number | null {
  const index = (HEADING_PARAGRAPH_STYLES as readonly ParagraphStyle[]).indexOf(style);
  return index < 0 ? null : index + 1;
}

/**
 * THE ONE AUTHORITATIVE STRING of a block, or `null` for a kind that has none.
 *
 * `authors-api.yaml` on `TextBlock.body`: "`body` is the one authoritative string every
 * offset in the system is measured over." A divider has no text at all (`ck_awb_divider` forces all
 * five text columns NULL); an image block's caption is "Held in the block's own text"
 * (`:4385-4389`), which is the `body` column and is why `caption` is the member name on the wire and
 * `body` is the column.
 */
export function blockBody(block: Block): string | null {
  switch (block.kind) {
    case 'text':
    case 'quote':
      return block.body;
    case 'image':
      return block.caption ?? null;
    case 'divider':
    case 'video':
      return null;
  }
}

/**
 * The marks declared on a block, or an empty list for a kind that carries none.
 *
 * An image's caption and a divider have NO mark rows on this surface - `ImageBlock` and
 * `DividerBlock` declare no `marks` member (`authors-api.yaml`) - and
 * `article-editor.service.ts`'s `toBlockWrites` writes `marks: []` for both.
 */
export function blockMarks(block: Block): readonly InlineMark[] {
  switch (block.kind) {
    case 'text':
    case 'quote':
      return block.marks ?? [];
    case 'image':
    case 'divider':
    case 'video':
      return [];
  }
}

/**
 * The paragraph rows declared on a block.
 *
 * ★ ONLY A TEXT BLOCK HAS THEM, and that is the contract rather than a simplification: `TextBlock`
 * is the only branch declaring `paragraphs` (`authors-api.yaml`) because Q31a made a text
 * block "hold several paragraphs" and left every other kind one string. A quote's body is one
 * run - D21 counts it as one thing (`04-schema.md`) - and the writers in `src/` agree:
 * `article-editor.service.ts` and `article-content-edit.service.ts` both write `paragraphs: []` on
 * quote, image and divider.
 */
export function blockParagraphs(block: Block): readonly Paragraph[] {
  return block.kind === 'text' ? (block.paragraphs ?? []) : [];
}
